import { lazy, Suspense } from 'react';

import { Route, Routes } from 'react-router-dom';

import { LoadingState } from '@/components/ui/Feedback';
import { useSessionBootstrap, useSessionExpiryListener } from '@/hooks/useAuth';
import { DashboardLayout } from '@/layouts/DashboardLayout';
import { HomeRoute, ProtectedRoute, PublicOnlyRoute } from '@/routes/ProtectedRoute';
import {
  facility as facilityDescriptor,
  gallery as galleryDescriptor,
  quote as quoteDescriptor,
  RESOURCES,
} from '@/config/resources';

const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const NewsletterListPage = lazy(() => import('@/pages/newsletter/NewsletterListPage'));
const NewsletterFormPage = lazy(() => import('@/pages/newsletter/NewsletterFormPage'));
const GalleryFormPage = lazy(() => import('@/pages/gallery/GalleryFormPage'));
const QuoteFormPage = lazy(() => import('@/pages/quote/QuoteFormPage'));
const FacilityFormPage = lazy(() => import('@/pages/facility/FacilityFormPage'));
const ProductionPage = lazy(() => import('@/pages/production/ProductionPage'));
const MediaLibraryPage = lazy(() => import('@/pages/media/MediaLibraryPage'));
const ProfilePage = lazy(() => import('@/pages/profile/ProfilePage'));
const ChangePasswordPage = lazy(() => import('@/pages/profile/ChangePasswordPage'));
const UsersPage = lazy(() => import('@/pages/users/UsersPage'));
const AccessPage = lazy(() => import('@/pages/access/AccessPage'));
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'));
const BannerPlacementPage = lazy(() => import('@/pages/banner/BannerPlacementPage'));
const ResourceListPage = lazy(() => import('@/pages/resource/ResourceListPage'));
const ResourceFormPage = lazy(() => import('@/pages/resource/ResourceFormPage'));
const ForbiddenPage = lazy(() => import('@/pages/ForbiddenPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

function RouteFallback() {
  return <LoadingState />;
}

export function App() {
  useSessionBootstrap();
  useSessionExpiryListener();

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route
          path="/login"
          element={
            <PublicOnlyRoute>
              <LoginPage />
            </PublicOnlyRoute>
          }
        />

        <Route
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route
            index
            element={
              <HomeRoute>
                <DashboardPage />
              </HomeRoute>
            }
          />

          <Route
            path="newsletters"
            element={
              <ProtectedRoute permission="newsletter:read">
                <NewsletterListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="newsletters/new"
            element={
              <ProtectedRoute permission="newsletter:create">
                <NewsletterFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="newsletters/:id"
            element={
              <ProtectedRoute permission="newsletter:update">
                <NewsletterFormPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="galleries"
            element={
              <ProtectedRoute permission="gallery:read">
                <ResourceListPage descriptor={galleryDescriptor} />
              </ProtectedRoute>
            }
          />
          <Route
            path="galleries/new"
            element={
              <ProtectedRoute permission="gallery:create">
                <GalleryFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="galleries/:id"
            element={
              <ProtectedRoute permission="gallery:update">
                <GalleryFormPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="quotes"
            element={
              <ProtectedRoute permission="quote:read">
                <ResourceListPage descriptor={quoteDescriptor} />
              </ProtectedRoute>
            }
          />
          <Route
            path="quotes/new"
            element={
              <ProtectedRoute permission="quote:create">
                <QuoteFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="quotes/:id"
            element={
              <ProtectedRoute permission="quote:update">
                <QuoteFormPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="facilities"
            element={
              <ProtectedRoute permission="facility:read">
                <ResourceListPage descriptor={facilityDescriptor} />
              </ProtectedRoute>
            }
          />
          <Route
            path="facilities/new"
            element={
              <ProtectedRoute permission="facility:create">
                <FacilityFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="facilities/:id"
            element={
              <ProtectedRoute permission="facility:update">
                <FacilityFormPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="banners/placement"
            element={
              <ProtectedRoute permission="banner:read">
                <BannerPlacementPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="production"
            element={
              <ProtectedRoute permission="production:read">
                <ProductionPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="profile/password"
            element={
              <ProtectedRoute>
                <ChangePasswordPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="media"
            element={
              <ProtectedRoute permission="media:read">
                <MediaLibraryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="users"
            element={
              <ProtectedRoute permission="user:read">
                <UsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="access"
            element={
              <ProtectedRoute permission="role:read">
                <AccessPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="settings"
            element={
              <ProtectedRoute permission="setting:read">
                <SettingsPage />
              </ProtectedRoute>
            }
          />

          {RESOURCES.map((descriptor) => (
            <Route key={descriptor.key} path={descriptor.key}>
              <Route
                index
                element={
                  <ProtectedRoute permission={`${descriptor.permission}:read`}>
                    <ResourceListPage descriptor={descriptor} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="new"
                element={
                  <ProtectedRoute permission={`${descriptor.permission}:create`}>
                    <ResourceFormPage descriptor={descriptor} />
                  </ProtectedRoute>
                }
              />
              <Route
                path=":id"
                element={
                  <ProtectedRoute permission={`${descriptor.permission}:update`}>
                    <ResourceFormPage descriptor={descriptor} />
                  </ProtectedRoute>
                }
              />
            </Route>
          ))}

          <Route path="forbidden" element={<ForbiddenPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
